import { summarizeReport } from "./report-projection.ts";
import type { HubRun } from "./hub-catalog.ts";
import type { PresenterView, RunView } from "./types.ts";

export type HubChanges = Omit<HubRun, "contexts" | "datasetValues">;
export type PresenterUpdate = Omit<PresenterView, "state"> & {
  state: Omit<PresenterView["state"], "hubRun"> & { hubRun?: HubChanges };
  hubCapture: "attached";
};
export type RunUpdate = RunView | PresenterUpdate;

/** Facts are sent on the initial read, then attached locally to mutable updates. */
export function compactPresenter(view: PresenterView): PresenterUpdate {
  const { hubRun, reportPublications, ...state } = view.state;
  const changes = hubRun && mutableHub(hubRun);
  return {
    ...view,
    hubCapture: "attached",
    state: {
      ...state,
      ...(reportPublications
        ? {
            reportSummaries: Object.fromEntries(
              Object.entries(reportPublications).map(([id, publication]) => [
                id,
                summarizeReport(publication),
              ]),
            ),
          }
        : {}),
      ...(changes ? { hubRun: changes } : {}),
    },
  };
}
export function mutableHub({
  contexts: _contexts,
  datasetValues: _data,
  ...changes
}: HubRun): HubChanges {
  return changes;
}
export function hydrateRunUpdate(
  update: RunUpdate,
  previous: RunView | null,
): RunView {
  if (!("hubCapture" in update)) return update;
  if (
    previous?.role !== "presenter" ||
    previous.state.id !== update.state.id ||
    !previous.state.hubRun
  )
    throw Error("Read the captured run before attaching a live update.");
  const { hubCapture: _capture, state, ...view } = update;
  const { hubRun, ...rest } = state;
  return {
    ...view,
    state: {
      ...rest,
      ...(hubRun
        ? {
            hubRun: {
              ...hubRun,
              contexts: previous.state.hubRun.contexts,
              datasetValues: previous.state.hubRun.datasetValues,
            },
          }
        : {}),
    },
  };
}
/** Freeze once at capture/hydration; domain transitions only mutate separate run decisions. */
export function freezeCapture<T>(value: T): T {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    for (const item of Object.values(value)) freezeCapture(item);
    Object.freeze(value);
  }
  return value;
}
